import { useCallback, useEffect, useRef } from "react"

export default function InfiniteScroll({
  isFetchingNextPage,
  fetchNextPage,
  hasNextPage,
  root,
}: {
  isFetchingNextPage: boolean
  fetchNextPage: () => void
  hasNextPage: boolean
  root?: Element | null
}) {
  const loadMoreRef = useRef<HTMLDivElement>(null)

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  )

  useEffect(() => {
    const element = loadMoreRef.current
    if (!element) return

    const observer = new IntersectionObserver(handleObserver, {
      root: root || null,
      rootMargin: "100px",
      threshold: 0.1,
    })

    observer.observe(element)
    return () => observer.disconnect()
  }, [handleObserver, root])

  return (
    <>
      {hasNextPage && (
        <>
          <div ref={loadMoreRef} className="h-4 w-full" />
          {isFetchingNextPage && (
            <div className="fixed right-8 bottom-8 z-50">
              <div className="bg-foreground text-background animate-in slide-in-from-bottom-2 flex items-center gap-3 rounded-md px-4 py-3 shadow-lg">
                <div className="border-background/30 border-t-background h-4 w-4 animate-spin rounded-full border-2" />
                <span className="text-sm font-medium">Loading more...</span>
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}
